/* @flow */


import openrtbbase from './openrtbbase';
import { INIT_STATUS } from './bidbase';
import { logError } from '../utils/logger';
import { extend, isNumber, buildUrl } from '../utils/coreutils';
import { getRTBUserEids } from '../idproviders/idputils';
import { apiName } from '../utils';
import adconfig from '../adconfig';

/**
 * @typedef {object} hbPosData
 */

const BID_URL = 'https://htlb.casalemedia.com/openrtb/pbjs';
const IFRAME_USER_SYNC_URL = 'https://js-sec.indexww.com/um/ixmatch.html';

/**
 * @module bidders/index
 * @requires module:logger
 * @requires module:coreutils
 */
/**
 * @global
 * @typedef {object} indexConfig
 * @description extends {@link hbConfig}
 */
/**
 * @description Implements functionality for index (casalemedia) header bidding
 * @augments _openrtbbase
 * @requires module:utils
 */
class _indexbid extends openrtbbase {
  _config: indexConfig;

  _DefConfig: indexConfig;

  constructor(name: string = 'index') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig);
    this._config = extend(true, {}, this._DefConfig);
    // Set OpenRTB version to 2.5 for Index Exchange
    this._openrtbVersion = 2.5;
    this._reqContentType = 'text/plain';
    // Include credentials for user sync
    this._reqWithCredentials = true;
    // Index Exchange sends bid prices in cents, need to convert to dollars
    // Reference: https://raw.githubusercontent.com/prebid/Prebid.js/refs/heads/master/modules/ixBidAdapter.js
    this._bidPriceInCents = true;
    // do not send display manager
    this._sendDisplayManager = true;
  }

  /**
   * - Checks partner specific config for index partner
   *
   * @typedef {object} initStatus
   * @returns {initStatus} - Init status
   * @access private
   */
  _configCheck(): initStatus {
    // index requires siteID as number for each position
    const chk = Object.keys(this._config.posTokenMap).every((pos: string) => {
      if (!isNumber(this._config.posTokenMap[pos].siteID)) {
        logError(
          `${this._name} config error: posTokenMap for ${pos} for property siteID is not number`
        );
        return false;
      }
      return true;
    });

    return chk ? super._configCheck() : INIT_STATUS.F_FAILED;
  }

  /**
   * Get endpoint URL with siteID parameter from first requested position
   * @param {Array<string>} posList - List of positions being requested
   * @returns {string} - Endpoint URL
   * @access private
   */
  _getEndpoint(posList: Array<string>): string {
    // Get siteID from first position being requested (not all configured positions)
    if (!posList.length) {
      return BID_URL;
    }
    const [firstPos] = posList;
    const { siteID } = this._config.posTokenMap[firstPos] || {};
    // Only add siteID query param if it's present and non-zero
    return siteID ? buildUrl(BID_URL, { s: siteID }) : BID_URL;
  }

  /**
   * Get impression ext fields
   * @param {string} pos - Position
   * @returns {object} - Impression ext object
   * @access private
   */
  _getImpExt(pos: string): anyObject {
    const gpid = adconfig.getGPID(pos);
    return {
      // eslint-disable-next-line camelcase
      dfp_ad_unit_code: gpid,
      gpid,
      data: {
        adserver: {
          name: 'gam',
          adslot: gpid
        }
      }
    };
  }

  /**
   * Get format ext with siteID
   * @param {string} pos - Position
   * @param {object} format - Format object
   * @returns {object} - Format ext object
   * @access private
   */
  // eslint-disable-next-line no-unused-vars
  _getImpFormatExt(pos: string, format: anyObject): anyObject {
    const { siteID } = this._config.posTokenMap[pos];
    return siteID ? { siteID } : {};
  }

  /**
   * Get request ext with source field
   * @returns {object} - Request ext object
   * @access private
   */
  _getRequestExt(): anyObject {
    return { source: apiName() };
  }

  /**
   * Get user extra fields (top-level user.eids for Prebid compatibility)
   * @returns {object} - User extra object
   * @access private
   */
  _getUserExtra(): anyObject {
    const { user: { eids = [] } = {} } = getRTBUserEids() || {};
    return eids.length ? { eids } : {};
  }

  /**
   * Get user sync objects
   * @returns {Array<syncObj>} - Array of sync objects
   * @access private
   */
  _getUserSync(): Array<syncObj> {
    const name = this.getName();
    const type = 'iframe';
    const url = IFRAME_USER_SYNC_URL;
    return [{ name, type, url }];
  }
}

const indexbid: _indexbid = new _indexbid();
export default indexbid;
